import React from 'react';

import { useTranslation } from '@modernloop/i18n';
import { getPrimaryAccentBackground, getPrimaryAccentContrastText } from '@modernloop/shared/theme';
import { Chip, Theme, Typography, keyframes, useTheme } from '@mui/material';

type Props = {
  delegateStatus: string;
};

const pulse = keyframes`
  0% { opacity: 0.7; }
  50% { opacity: 1; }
  100% { opacity: 0.7; }
`;

const HIDDEN_DELEGATE_STATUSES = new Set(['QUEUED', 'WAITING_ON_CANDIDATE', 'WAITING_ON_TEAM']);

type StatusConfig = {
  labelKey: string;
  animate?: boolean;
  getBackground: (theme: Theme) => string;
  getColor: (theme: Theme) => string;
};

const STATUS_CONFIG: Record<string, StatusConfig> = {
  ACTIVE: {
    labelKey: 'hiring-dashboard-schedule:schedule.delegateStatus.working',
    getBackground: getPrimaryAccentBackground,
    getColor: getPrimaryAccentContrastText,
    animate: true,
  },
  COMPLETED: {
    labelKey: 'hiring-dashboard-schedule:schedule.delegateStatus.completed',
    getBackground: (theme) => theme.palette.success.main,
    getColor: (theme) => theme.palette.success.contrastText,
  },
  STOPPED: {
    labelKey: 'hiring-dashboard-schedule:schedule.delegateStatus.stopped',
    getBackground: (theme) => theme.palette.secondary.dark,
    getColor: (theme) => theme.palette.secondary.contrastText,
  },
};

const getVisibleStatusConfig = (delegateStatus: string): StatusConfig | undefined => {
  if (HIDDEN_DELEGATE_STATUSES.has(delegateStatus)) {
    return undefined;
  }

  return STATUS_CONFIG[delegateStatus];
};

/** Resolves palette-backed chip colors for a delegate status, if visible. */
export const getDelegateStatusChipStyles = (
  delegateStatus: string,
  theme: Theme,
  config?: StatusConfig
): { background: string; color: string } | null => {
  const resolvedConfig = config ?? getVisibleStatusConfig(delegateStatus);
  if (!resolvedConfig) {
    return null;
  }

  return {
    background: resolvedConfig.getBackground(theme),
    color: resolvedConfig.getColor(theme),
  };
};

/** Chip showing AI delegate lifecycle status on schedule task details. */
const DelegateStatusChip: React.FC<Props> = ({ delegateStatus }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const config = getVisibleStatusConfig(delegateStatus);
  const styles = config ? getDelegateStatusChipStyles(delegateStatus, theme, config) : null;

  if (!config || !styles) {
    return null;
  }

  return (
    <Chip
      label={
        <Typography variant="body2" fontWeight={600} sx={{ color: styles.color }}>
          {t(config.labelKey)}
        </Typography>
      }
      size="small"
      sx={{
        py: 0.5,
        px: 1,
        height: 'auto',
        background: styles.background,
        color: styles.color,
        ...(config.animate
          ? {
              animation: `${pulse} 2s ease-in-out infinite`,
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }
          : {}),
      }}
    />
  );
};

export default DelegateStatusChip;
